import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    debounceTime,
    map,
    EMPTY,
    catchError,
    switchMap,
    take,
    takeUntil,
    tap,
    timer,
    of,
    filter,
    from,
} from 'rxjs'
import { ViewerService } from '../../case-viewer/neuroglancer-frame/viewer.service'
import { viewerActions } from './viewer.actions'
import {
    selectLayers,
    selectLayerListDialogOpen,
    selectSelectableAnnotationLayerUrls,
    selectSelectableSegmentationLayerNames,
    selectViewerState,
} from './viewer.selectors'
import { getColorForSelection } from '../../shared/utils/color-utils'
import { Store } from '@ngrx/store'
import {
    selectSelectionState,
    selectSavedAndCurrentFreeSelections,
    selectSavedHierarchicalSelections,
    selectSavedUnsupervisedSelections,
    selectHierarchicalSelection,
    selectUnsupervisedSelection,
} from '../selection/selection.selectors'
import { selectionActions } from '../selection/selection.actions'
import {
    getAllOwnAndChildrenPointsInNode,
    getEditableSelectionBuckets,
    getTotalPointsSize,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    SelectionBase,
    UnsupervisedSelectionRoot,
} from '../selection/selection.state'
import { mapViewerStateToNeuroglancerConfig, getLoadedLayerIdsFingerprint } from './viewer-state-utils'
import { defaultDebounceTime } from '../../shared/utils/rxjs-pipelines'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { RouterUtilsService } from '../../shared/services/router-utils.service'
import { userActions } from '../user/user.actions'
import { selectCaseId, selectLoadedSegSources } from '../case/case.selectors'
import { caseActions } from '../case/case.actions'

@Injectable()
export class ViewerEffects {
    private readonly jobSettingsCache = new Map<string, Promise<unknown>>()

    constructor(
        private readonly actions$: Actions,
        private readonly viewerService: ViewerService,
        private readonly datasetApi: DatasetApiService,
        private readonly store: Store,
        private readonly routerUtils: RouterUtilsService
    ) {}

    setQueryParamPosition$ = createEffect(
        () => {
            return this.viewerService.position$.pipe(
                defaultDebounceTime(),
                tap(position => {
                    if (position) {
                        this.routerUtils.updatePositionQueryParam([
                            position[0],
                            position[1],
                            position[2],
                        ])
                    }
                })
            )
        },
        { dispatch: false }
    )

    setQueryParamZoom$ = createEffect(
        () => {
            return this.viewerService.zoom$.pipe(
                defaultDebounceTime(),
                tap(zoom => {
                    if (zoom) this.routerUtils.updateZoomQueryParam(zoom)
                })
            )
        },
        { dispatch: false }
    )

    setQueryParamLayerConfig$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    viewerActions.setLayerOpacity,
                    viewerActions.setLayerVisibility,
                    viewerActions.setLocalLayerVisibility,
                    viewerActions.setLayerShaderColor,
                    viewerActions.setLayerOpacityUnselected,
                    viewerActions.setLayerSaturation,
                    viewerActions.setLayerBrightness,
                    viewerActions.setLayerContrast,
                    viewerActions.setLayerSegmentationColor
                ),
                defaultDebounceTime(),
                concatLatestFrom(() => this.store.select(selectLayers)),
                tap(([, layers]) => {
                    if (layers && layers.length > 0) {
                        this.routerUtils.updateLayerQueryParam(layers)
                    }
                })
            )
        },
        { dispatch: false }
    )

    updateSegmentationViewerSelection$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.updateSelection,
                    selectionActions.clearSelection,
                    selectionActions.setFreeSelection,
                    selectionActions.setHierarchicalSelectionRoot,
                    selectionActions.setHierarchicalSelectionNode
                ),
                concatLatestFrom(() => [
                    this.store.select(selectSelectionState),
                    this.store.select(selectSelectableSegmentationLayerNames),
                    this.store.select(selectLoadedSegSources),
                ]),
                tap(([_, selectionState, layerNames, loadedSegSources]) => {
                    const buckets = getEditableSelectionBuckets(selectionState)
                    const hasAnySelection = buckets.some(b => b.points.size > 0)
                    const loadedSegSourceSet = new Set(loadedSegSources)
                    const layers = layerNames.map((name: string) =>
                        this.viewerService.getSegmentationLayerByName(name)
                    )

                    layers.forEach(layer => {
                        if (!layer) return
                        const layerUrl = layer.dataSources![0]!.spec.url
                        const segmentIds = buckets
                            .filter(b => b.segSource === layerUrl)
                            .flatMap(b => [...b.points])
                        const segmentationGroupState =
                            layer.displayState.segmentationGroupState.value
                        segmentationGroupState.visibleSegments.clear()
                        if (segmentIds.length > 0) {
                            segmentationGroupState.visibleSegments.add(
                                segmentIds.map(id => BigInt(id)) as bigint[]
                            )
                            // Non-empty set: ignoreNullVisibleSet does not matter.
                            layer.displayState.ignoreNullVisibleSet.value = true
                        } else if (hasAnySelection && loadedSegSourceSet.has(layerUrl)) {
                            // Empty visible set + ignoreNullVisibleSet=true shows ALL segments
                            // in Neuroglancer. For a loaded intensity map with no selected
                            // cells (e.g. lasso only on mapping id=1), hide the layer instead.
                            layer.displayState.ignoreNullVisibleSet.value = false
                        } else {
                            // No selection (or unrelated seg layer): restore default show-all.
                            layer.displayState.ignoreNullVisibleSet.value = true
                        }
                    })
                }),
                catchError(err => {
                    console.error('Error fetching marker mapping:', err)
                    return EMPTY
                })
            )
        },
        { dispatch: false }
    )

    reRenderAnnotationLayer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.loadingFreeSelectionsSucceeded,
                    selectionActions.loadingHierarchicalSelectionsSucceeded,
                    selectionActions.loadingUnsupervisedSelectionsSucceeded,
                    selectionActions.setFreeSelection,
                    selectionActions.updateSelection,
                    selectionActions.clearSelection,
                    // TODO: updating only after saving makes the app less snappy - use triggering action and revert on fail?
                    selectionActions.savingFreeSelectionSucceeded,
                    selectionActions.renamingFreeSelectionSucceeded,
                    selectionActions.renamingHierarchicalSelectionRootSucceeded,
                    selectionActions.renamingHierarchicalSelectionSucceeded,
                    selectionActions.deletingFreeSelectionSucceeded,
                    selectionActions.deletingHierarchicalSelectionRootSucceeded,
                    selectionActions.deletingAutoCellTypeDetectionRootSucceeded,
                    selectionActions.savingHierarchicalSelectionRootSucceeded,
                    selectionActions.savingAutoCellTypeDetectionRootSucceeded,
                    selectionActions.setSelectionVisibility,
                    selectionActions.setSelectionOpacity,
                    selectionActions.setSelectionShaderColor,
                    viewerActions.configLoaded,
                    viewerActions.syncViewerConfig,
                    caseActions.toggleLoadedSegObjectPropertyMap
                ),
                // Wait for Neuroglancer layers after config restore / map toggle.
                debounceTime(100),
                concatLatestFrom(() => [
                    this.store.select(selectSelectableAnnotationLayerUrls),
                    this.store.select(selectSavedAndCurrentFreeSelections),
                    this.store.select(selectSavedHierarchicalSelections),
                    this.store.select(selectSavedUnsupervisedSelections),
                    this.store.select(selectHierarchicalSelection),
                    this.store.select(selectUnsupervisedSelection),
                    this.store.select(selectLoadedSegSources),
                ]),
                tap(
                    ([
                        ,
                        layerSources,
                        freeSelections,
                        hierarchicalRoots,
                        unsupervisedRoots,
                        hierarchicalSelection,
                        unsupervisedSelection,
                        loadedSegSources,
                    ]) => {
                    const unsavedLevels: SelectionBase[] = []
                    if (getTotalPointsSize(hierarchicalSelection.unsavedLevel) > 0) {
                        unsavedLevels.push(hierarchicalSelection.unsavedLevel)
                    }
                    if (getTotalPointsSize(unsupervisedSelection.unsavedLevel) > 0) {
                        unsavedLevels.push(unsupervisedSelection.unsavedLevel)
                    }
                    this.updateAnnotationLayerSelections(
                        freeSelections,
                        unsavedLevels,
                        [...hierarchicalRoots.values()],
                        [...unsupervisedRoots.values()],
                        layerSources,
                        loadedSegSources
                    )
                }
                )
            )
        },
        { dispatch: false }
    )

    updateViewerConfig$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(viewerActions.configLoaded, viewerActions.syncViewerConfig),
                concatLatestFrom(() => this.store.select(selectViewerState)),
                switchMap(([, state]) => {
                    const generation = this.viewerService.startConfigSync()
                    const loadedLayerSnapshot = getLoadedLayerIdsFingerprint(state)

                    return from(mapViewerStateToNeuroglancerConfig(state)).pipe(
                        concatLatestFrom(() => this.store.select(selectViewerState)),
                        tap(([config, latestState]) => {
                            if (!this.viewerService.isConfigSyncGenerationCurrent(generation)) {
                                return
                            }

                            const latestFingerprint = getLoadedLayerIdsFingerprint(latestState)
                            if (latestFingerprint !== loadedLayerSnapshot) {
                                if (!latestState.layerListDialogOpen) {
                                    this.store.dispatch(viewerActions.syncViewerConfig({}))
                                }
                                this.viewerService.finishConfigSync(generation)
                                return
                            }

                            this.viewerService.setViewerConfig(config)
                            this.viewerService.finishConfigSync(generation)
                        }),
                        catchError(err => {
                            console.error('Error building Neuroglancer config:', err)
                            this.viewerService.finishConfigSync(generation)
                            return EMPTY
                        })
                    )
                })
            )
        },
        { dispatch: false }
    )

    saveViewerStateOnLayerListDialogClose$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(viewerActions.setLayerListDialogOpen),
                filter(({ open, layersChanged }) => !open && !!layersChanged),
                switchMap(() => of(null).pipe(this.datasetApi.saveViewerState()))
            )
        },
        { dispatch: false }
    )

    postViewerStateOnStateChanged$ = createEffect(
        () => {
            return this.store
                .select(selectCaseId)
                .pipe(
                    switchMap(() =>
                        this.actions$.pipe(
                            ofType(
                                viewerActions.setZoom,
                                viewerActions.setPosition,
                                viewerActions.setExpandedNodes,
                                viewerActions.setLayout,
                                viewerActions.setLayerOpacity,
                                viewerActions.setLayerVisibility,
                                viewerActions.setLocalLayerVisibility,
                                viewerActions.setLayerShaderColor,
                                viewerActions.setLayerSaturation,
                                viewerActions.setLayerOpacityUnselected,
                                viewerActions.setLayerBrightness,
                                viewerActions.setLayerContrast,
                                viewerActions.setLayerNormalize,
                                viewerActions.setLayerSegmentationColor
                            ),
                            concatLatestFrom(() => this.store.select(selectLayerListDialogOpen)),
                            filter(
                                ([action, layerListDialogOpen]) =>
                                    !(
                                        layerListDialogOpen &&
                                        action.type === viewerActions.setLocalLayerVisibility.type
                                    )
                            ),
                            debounceTime(2000),
                            this.datasetApi.saveViewerState()
                        )
                    )
                )
        },
        { dispatch: false }
    )

    postViewerStateOnLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.startLogout),
            this.datasetApi.saveViewerState(),
            map(() => userActions.logoutViewerStateSent())
        )
    })

    disposeViewer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(userActions.logoutFinished),
                tap(() => {
                    this.viewerService.disposeViewer()
                })
            )
        },
        { dispatch: false }
    )

    private updateAnnotationLayerSelections(
        freeSelections: SelectionBase[],
        unsavedLevels: SelectionBase[],
        hierarchicalRoots: HierarchicalSelectionRoot[],
        unsupervisedRoots: UnsupervisedSelectionRoot[],
        layerSources: string[],
        loadedSegSources: string[] = []
    ): void {
        const loadedSegSourceSet = new Set(loadedSegSources)
        layerSources.forEach(source =>
            this.viewerService
                .getAnnotationLayerNameBySource$(source)
                .pipe(take(1), takeUntil(timer(5000)))
                .subscribe(layerName => {
                    if (layerName) {
                        this.viewerService
                            .getAnnotationLayerByName$(layerName)
                            .pipe(take(1))
                            .subscribe(async renderLayer => {
                                // TODO: move jobSettings to a state, request is slow
                                const jobSettingsUrl =
                                    renderLayer.userLayer?.dataSources[0]?.spec?.url.replace(
                                        'precomputed://',
                                        ''
                                    ) + '/job_settings.json'
                                const jobSettings = (await this.getJobSettings(
                                    jobSettingsUrl
                                )) as { seg_url: string }

                                const segUrl = jobSettings.seg_url
                                const matchesLoadedMap =
                                    !!segUrl && loadedSegSourceSet.has(segUrl)

                                const id2color = new Map<number, number>()

                                // Own-bucket paints first (free / unsaved leftovers).
                                for (const selection of [...freeSelections, ...unsavedLevels]) {
                                    this.applyOwnBucketAnnotationColors(
                                        id2color,
                                        selection,
                                        segUrl
                                    )
                                }
                                // Hierarchical / unsupervised: each visible level paints its
                                // own points ∪ all descendants. Parent-before-child walk so
                                // later child colors win for shared segment IDs.
                                for (const root of [
                                    ...hierarchicalRoots,
                                    ...unsupervisedRoots,
                                ]) {
                                    this.applyHierarchicalTreeAnnotationColors(
                                        id2color,
                                        root,
                                        segUrl
                                    )
                                }

                                const managedLayer = renderLayer.userLayer?.managedLayer
                                // Show COM layer when an intensity map is loaded, or when a
                                // visible (e.g. saved) selection has points for this seg source.
                                if (
                                    managedLayer &&
                                    !managedLayer.visible &&
                                    (matchesLoadedMap || id2color.size > 0)
                                ) {
                                    managedLayer.setVisible(true)
                                }

                                // PoC hack (see: src/neuroglancer/annotation/renderlayer.ts)
                                renderLayer.getPropertiesForId = (id: number) => {
                                    // Performance-critical: changing active selection or toggling opacity
                                    return id2color.get(id) ?? 0
                                }
                                // Trigger redraw after updating properties
                                renderLayer.redrawNeeded.dispatch()
                            })
                    }
                })
        )
    }

    /** Paint a selection's own buckets only (partition leftovers / free selections). */
    private applyOwnBucketAnnotationColors(
        id2color: Map<number, number>,
        selection: SelectionBase,
        segUrl: string
    ): void {
        if (!selection.annotationVisible || selection.annotationOpacity <= 0) {
            return
        }
        const color = getColorForSelection(selection)
        for (const bucket of selection.buckets) {
            if (bucket.segSource !== segUrl) continue
            for (const id of bucket.points) {
                id2color.set(id, color)
            }
        }
    }

    /**
     * Paint a hierarchical/unsupervised tree for Neuroglancer.
     * Storage remains a partition (own buckets = leftovers); visualization treats a visible
     * level as representing own ∪ descendants. DFS parent-before-child so child colors win.
     */
    private applyHierarchicalTreeAnnotationColors(
        id2color: Map<number, number>,
        root: HierarchicalSelectionRoot | UnsupervisedSelectionRoot,
        segUrl: string
    ): void {
        const visit = (node: HierarchicalSelectionNode): void => {
            if (node.annotationVisible && node.annotationOpacity > 0) {
                const color = getColorForSelection(node)
                for (const id of getAllOwnAndChildrenPointsInNode(node, segUrl)) {
                    id2color.set(id, color)
                }
            }
            for (const child of node.children ?? []) {
                visit(child)
            }
        }
        for (const child of root.children ?? []) {
            visit(child)
        }
    }

    private getJobSettings(url: string): Promise<unknown> {
        if (!this.jobSettingsCache.has(url)) {
            this.jobSettingsCache.set(
                url,
                fetch(url)
                    .then(res => res.json())
                    .catch(err => {
                        this.jobSettingsCache.delete(url)
                        throw err
                    })
            )
        }
        return this.jobSettingsCache.get(url)!
    }
}
